// src/contexts/ChatContext.jsx - ИСПРАВЛЕННАЯ ВЕРСИЯ
import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { useSafeTimeout } from '../hooks/useSafeTimeout';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { chatAPI, usersAPI } from '../services/api.js';
import { CONSTANTS } from '../constants';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const [failedMessages, setFailedMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [replyTo, setReplyTo] = useState(null);
  const [pendingMarkRead, setPendingMarkRead] = useState(new Map());

  const { user } = useAuth();
  const { subscribe, unsubscribe, send, isConnected } = useWebSocket();
  const { setSafeTimeout, isMounted } = useSafeTimeout();
  const { isVisible, isHidden } = usePageVisibility();

  // Refs
  const messageIdsRef = useRef(new Set());
  const pendingMessagesRef = useRef(new Map());
  const failedMessagesRef = useRef(new Set());
  const isLoadingMoreRef = useRef(false);
  const messageQueueRef = useRef([]);
  const userIdRef = useRef(user?.id);
  const lastProcessedMessageIdRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const processedMessageIdsRef = useRef(new Set()); // ✅ Для отслеживания обработанных сообщений

  // Синхронизируем реф с актуальным user.id
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  // Подсчет общего количества непрочитанных сообщений
  useEffect(() => {
    const total = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
    setTotalUnreadCount(total);
  }, [unreadCounts]);

  // Ограничиваем размер Set
  const cleanupMessageIds = useCallback(() => {
    if (messageIdsRef.current.size > CONSTANTS.LIMITS.USERS_CACHE_SIZE) {
      const array = Array.from(messageIdsRef.current);
      messageIdsRef.current = new Set(array.slice(-500));
    }
    if (processedMessageIdsRef.current.size > 1000) {
      const array = Array.from(processedMessageIdsRef.current);
      processedMessageIdsRef.current = new Set(array.slice(-500));
    }
  }, []);

  // Подписка на канал конкретной комнаты
  const subscribeToRoomChannel = useCallback((roomId) => {
    if (!isConnected || !roomId) return;
    const channel = `chat:${roomId}`;
    send({
      type: 'subscribe',
      data: { channels: [channel] }
    });
  }, [isConnected, send]);

  // Отписка от канала комнаты
  const unsubscribeFromRoomChannel = useCallback((roomId) => {
    if (!isConnected || !roomId) return;
    const channel = `chat:${roomId}`;
    send({
      type: 'unsubscribe',
      data: { channels: [channel] }
    });
  }, [isConnected, send]);

  // Подписка на каналы всех комнат
  const subscribeToAllRooms = useCallback(() => {
    if (!isConnected || rooms.length === 0) return;
    const channels = rooms.map(room => `chat:${room.id}`);
    send({
      type: 'subscribe',
      data: { channels }
    });
  }, [isConnected, rooms, send]);

  // Загрузка списка комнат
  const loadRooms = useCallback(async (force = false) => {
    if (!user) return;
    
    if (!force && rooms.length > 0 && initialLoadDoneRef.current) {
      return;
    }
    
    setLoadingRooms(true);
    setError('');
    
    try {
      const data = await chatAPI.getRooms();
      
      if (isMounted.current) {
        setRooms(data || []);
        
        const counts = {};
        data.forEach(room => {
          if (room.unread_count > 0) {
            counts[room.id] = room.unread_count;
          }
        });
        
        setUnreadCounts(counts);
        initialLoadDoneRef.current = true;
      }
    } catch (err) {
      console.error('Error loading rooms:', err);
      if (isMounted.current) {
        setError('Не удалось загрузить список чатов');
      }
    } finally {
      if (isMounted.current) {
        setLoadingRooms(false);
      }
    }
  }, [user, isMounted, rooms.length]);

  // При изменении списка комнат подписываемся на их каналы
  useEffect(() => {
    if (rooms.length > 0) {
      subscribeToAllRooms();
    }
  }, [rooms, subscribeToAllRooms]);

  // Загрузка сообщений комнаты
const loadMessages = useCallback(async (roomId, skip = 0, limit = 50, appendToStart = false) => {
    if (!roomId) return;
    
    if (!appendToStart) {
        setLoadingMessages(true);
        setMessages([]);
        setHasMoreMessages(true);
        setMessagesOffset(0);
        messageIdsRef.current.clear();
        pendingMessagesRef.current.clear();
    } else {
        setLoadingMoreMessages(true);
    }
    setError('');
    
    try {
        console.log(`📚 Loading messages for room ${roomId}, skip=${skip}, limit=${limit}`);
        const data = await chatAPI.getMessages(roomId, { skip, limit });
        
        if (!data || !Array.isArray(data)) {
            console.warn('No messages data received');
            if (isMounted.current) {
                if (appendToStart) {
                    setLoadingMoreMessages(false);
                } else {
                    setLoadingMessages(false);
                }
            }
            return;
        }
        
        // Обогащаем сообщения информацией о пользователях
        const enrichedMessages = await Promise.all(
            data.map(async (msg) => {
                // Системные сообщения
                if (!msg.user_id) {
                    return {
                        ...msg,
                        user_name: 'Система',
                        author: null
                    };
                }
                
                // Свои сообщения
                if (msg.user_id === user?.id) {
                    return {
                        ...msg,
                        user_name: user?.name || 'Вы',
                        author: user
                    };
                }
                
                // Чужие сообщения - пытаемся загрузить пользователя
                try {
                    const userData = await usersAPI.getUser(msg.user_id).catch(err => {
                        console.warn(`Failed to load user ${msg.user_id}:`, err);
                        return null;
                    });
                    
                    if (userData) {
                        const firstName = userData.telegram_first_name || '';
                        const lastName = userData.telegram_last_name || '';
                        
                        let userName = userData.name;
                        if (firstName && lastName) {
                            userName = `${firstName} ${lastName}`;
                        } else if (firstName) {
                            userName = firstName;
                        } else if (lastName) {
                            userName = lastName;
                        } else if (userData.telegram_username) {
                            userName = `@${userData.telegram_username}`;
                        }
                        
                        return {
                            ...msg,
                            user_name: userName,
                            author: userData
                        };
                    }
                } catch (err) {
                    console.warn(`Failed to load user ${msg.user_id}:`, err);
                }
                
                return {
                    ...msg,
                    user_name: 'Пользователь',
                    author: null
                };
            })
        );
        
        // Сохраняем ID сообщений
        enrichedMessages.forEach(msg => {
            if (msg.id) messageIdsRef.current.add(msg.id);
        });
        
        // Переворачиваем для правильного порядка
        const reversed = [...enrichedMessages].reverse();
        
        if (isMounted.current) {
            if (appendToStart) {
                setMessages(prev => [...reversed, ...prev]);
                setMessagesOffset(prev => prev + limit);
            } else {
                setMessages(reversed);
                setMessagesOffset(limit);
            }
            
            if (data.length < limit) {
                setHasMoreMessages(false);
            }
        }
        
        cleanupMessageIds();
    } catch (err) {
        console.error('Error loading messages:', err);
        if (isMounted.current) {
            setError('Не удалось загрузить сообщения');
        }
    } finally {
        if (isMounted.current) {
            if (appendToStart) {
                setLoadingMoreMessages(false);
            } else {
                setLoadingMessages(false);
            }
        }
    }
}, [user?.id, cleanupMessageIds, isMounted]);


  // Отправка сообщения
  const sendMessageRest = useCallback(async (roomId, content, attachments = []) => {
    if (!roomId || !content.trim()) return null;

    if (!user) {
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const tempMessage = {
        id: tempId,
        room_id: roomId,
        user_id: null,
        user_name: 'Вы',
        content: content.trim(),
        attachments,
        is_system: false,
        created_at: new Date().toISOString(),
        status: 'queued',
        reply_to: replyTo ? { 
          id: replyTo.id, 
          user_name: replyTo.user_name,
          content: replyTo.content 
        } : null
      };

      messageQueueRef.current.push({
        roomId,
        content: content.trim(),
        attachments,
        replyTo: replyTo ? { ...replyTo } : null,
        tempId,
        createdAt: Date.now()
      });

      if (isMounted.current) {
        setMessages(prev => [...prev, tempMessage]);
        setReplyTo(null);
      }

      localStorage.setItem('message_queue', JSON.stringify(messageQueueRef.current));
      return { id: tempId, queued: true };
    }
    
    setSendingMessage(true);
    
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const tempMessage = {
      id: tempId,
      room_id: roomId,
      user_id: user?.id,
      user_name: user?.name || 'Вы',
      content: content.trim(),
      attachments,
      is_system: false,
      created_at: new Date().toISOString(),
      status: 'sending',
      reply_to: replyTo ? { 
        id: replyTo.id, 
        user_name: replyTo.user_name,
        content: replyTo.content 
      } : null
    };
    
    if (!isConnected) {
      messageQueueRef.current.push({
        roomId,
        content: content.trim(),
        attachments,
        replyTo: replyTo ? { ...replyTo } : null,
        tempId,
        createdAt: Date.now()
      });
      
      if (isMounted.current) {
        setMessages(prev => [...prev, { ...tempMessage, status: 'queued' }]);
        setReplyTo(null);
      }
      
      setSendingMessage(false);
      localStorage.setItem('message_queue', JSON.stringify(messageQueueRef.current));
      return { id: tempId, queued: true };
    }
    
    pendingMessagesRef.current.set(tempId, {
      content: content.trim(),
      roomId,
      attachments,
      replyTo: replyTo ? { ...replyTo } : null,
      created_at: tempMessage.created_at
    });
    
    if (isMounted.current) {
      setMessages(prev => [...prev, tempMessage]);
      setReplyTo(null);
    }
    
    try {
      const messageData = { 
        content: content.trim(), 
        attachments,
        reply_to_id: replyTo?.id 
      };
      
      const message = await chatAPI.sendMessage(roomId, messageData);
      
      if (isMounted.current) {
        setMessages(prev => 
          prev.map(msg => msg.id === tempId ? { 
            ...message, 
            user_name: message.user_name || user?.name 
          } : msg)
        );
      }
      
      if (message.id) {
        messageIdsRef.current.add(message.id);
        pendingMessagesRef.current.delete(tempId);
      }
      
      if (isMounted.current) {
        setRooms(prevRooms => {
          const roomIndex = prevRooms.findIndex(r => r.id === roomId);
          if (roomIndex === -1) return prevRooms;

          const updatedRoom = {
            ...prevRooms[roomIndex],
            last_message: message,
            updated_at: message.created_at
          };
          const newRooms = [...prevRooms];
          newRooms.splice(roomIndex, 1);
          newRooms.unshift(updatedRoom);
          return newRooms;
        });
      }
      
      cleanupMessageIds();
      return message;
      
    } catch (err) {
      console.error('Error sending message:', err);
      
      if (isMounted.current) {
        setMessages(prev => 
          prev.map(msg => msg.id === tempId ? { ...msg, status: 'failed' } : msg)
        );
      }
      
      setFailedMessages(prev => [...prev, tempId]);
      failedMessagesRef.current.add(tempId);
      pendingMessagesRef.current.delete(tempId);
      
      setError('Не удалось отправить сообщение');
      return null;
      
    } finally {
      setSendingMessage(false);
    }
  }, [user, isConnected, replyTo, cleanupMessageIds, isMounted]);

  // Повторная отправка failed сообщения
  const retrySendMessage = useCallback(async (tempId, roomId, content) => {
    setFailedMessages(prev => prev.filter(id => id !== tempId));
    failedMessagesRef.current.delete(tempId);
    setMessages(prev => prev.filter(msg => msg.id !== tempId));
    await sendMessageRest(roomId, content);
  }, [sendMessageRest]);

  // Загрузка количества непрочитанных
  const loadUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const response = await chatAPI.getUnreadCount();
      setTotalUnreadCount(response.unread_count);
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  }, [user]);

  // Загружаем при монтировании
  useEffect(() => {
    if (user) {
      loadUnreadCount();
      loadRooms(true);
    }
  }, [user, loadUnreadCount, loadRooms]);

  // Обработка очереди сообщений
  useEffect(() => {
    if (!isConnected || !isMounted.current || !user) return;
    
    const processQueue = async () => {
      if (messageQueueRef.current.length === 0) return;
      
      const queue = [...messageQueueRef.current];
      messageQueueRef.current = [];
      localStorage.removeItem('message_queue');
      
      for (const queued of queue) {
        setReplyTo(queued.replyTo);
        await sendMessageRest(queued.roomId, queued.content, queued.attachments);
        setReplyTo(null);
      }
    };
    
    processQueue();
  }, [isConnected, user, sendMessageRest, isMounted]);

  // Загрузка очереди из localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('message_queue');
      if (saved) {
        messageQueueRef.current = JSON.parse(saved);
      }
    } catch (err) {
      console.error('Error loading message queue:', err);
    }
  }, []);

  // Создание групповой комнаты
  const createGroupRoom = useCallback(async (name, participantIds) => {
    setLoadingRooms(true);
    setError('');
    try {
      const room = await chatAPI.createGroupRoom({ name, participant_ids: participantIds });
      if (isMounted.current) {
        setRooms(prev => {
          const exists = prev.some(r => r.id === room.id);
          if (!exists) {
            subscribeToRoomChannel(room.id);
            return [room, ...prev];
          }
          return prev;
        });
      }
      return room;
    } catch (err) {
      console.error('Error creating group:', err);
      setError('Не удалось создать группу');
      return null;
    } finally {
      if (isMounted.current) {
        setLoadingRooms(false);
      }
    }
  }, [subscribeToRoomChannel, isMounted]);

  // Создание или получение личной комнаты
  const getOrCreatePrivateRoom = useCallback(async (otherUserId) => {
    setLoadingRooms(true);
    setError('');
    try {
      const room = await chatAPI.getOrCreatePrivateRoom(otherUserId);
      if (isMounted.current) {
        setRooms(prev => {
          const exists = prev.some(r => r.id === room.id);
          if (!exists) {
            subscribeToRoomChannel(room.id);
            return [room, ...prev];
          }
          return prev;
        });
      }
      return room;
    } catch (err) {
      console.error('Error getting/creating private room:', err);
      setError('Не удалось создать чат с пользователем');
      return null;
    } finally {
      if (isMounted.current) {
        setLoadingRooms(false);
      }
    }
  }, [subscribeToRoomChannel, isMounted]);

  // Добавление участника в группу
  const addParticipant = useCallback(async (roomId, userId, role = 'member') => {
    setError('');
    try {
      await chatAPI.addParticipant(roomId, { user_id: userId, role });
      if (currentRoom?.id === roomId && isMounted.current) {
        const updatedRoom = await chatAPI.getRoom(roomId);
        setCurrentRoom(updatedRoom);
        setRooms(prev => prev.map(r => r.id === roomId ? updatedRoom : r));
      }
      return true;
    } catch (err) {
      console.error('Error adding participant:', err);
      setError('Не удалось добавить участника');
      return false;
    }
  }, [currentRoom, isMounted]);

  // Удаление участника из группы
  const removeParticipant = useCallback(async (roomId, userId) => {
    setError('');
    try {
      await chatAPI.removeParticipant(roomId, userId);
      if (currentRoom?.id === roomId && isMounted.current) {
        const updatedRoom = await chatAPI.getRoom(roomId);
        setCurrentRoom(updatedRoom);
        setRooms(prev => prev.map(r => r.id === roomId ? updatedRoom : r));
      }
      return true;
    } catch (err) {
      console.error('Error removing participant:', err);
      setError('Не удалось удалить участника');
      return false;
    }
  }, [currentRoom, isMounted]);

  // Покинуть группу
  const leaveRoom = useCallback(async (roomId) => {
    setError('');
    try {
      await chatAPI.leaveRoom(roomId);
      if (isMounted.current) {
        setRooms(prev => prev.filter(r => r.id !== roomId));
        if (currentRoom?.id === roomId) {
          setCurrentRoom(null);
          setMessages([]);
          setHasMoreMessages(true);
          setMessagesOffset(0);
          messageIdsRef.current.clear();
          pendingMessagesRef.current.clear();
        }
        unsubscribeFromRoomChannel(roomId);
      }
      return true;
    } catch (err) {
      console.error('Error leaving room:', err);
      setError('Не удалось покинуть комнату');
      return false;
    }
  }, [currentRoom, unsubscribeFromRoomChannel, isMounted]);

  // Выбор комнаты
  const selectRoom = useCallback(async (room) => {
    console.log('📍 Selecting room:', room.id);
    
    setUnreadCounts(prev => {
      const newCounts = { ...prev };
      delete newCounts[room.id];
      return newCounts;
    });

    setCurrentRoom(room);
    messageIdsRef.current.clear();
    pendingMessagesRef.current.clear();
    processedMessageIdsRef.current.clear(); // ✅ Очищаем при смене комнаты
    setReplyTo(null);
    
    await loadMessages(room.id, 0, 50, false);
    subscribeToRoomChannel(room.id);

    try {
      await chatAPI.markRoomAsRead(room.id);
      const response = await chatAPI.getUnreadCount();
      setTotalUnreadCount(response.unread_count);
    } catch (err) {
      console.error('Error marking room as read:', err);
    }
  }, [loadMessages, subscribeToRoomChannel]);

  // Сброс текущей комнаты
  const resetCurrentRoom = useCallback(() => {
    setCurrentRoom(null);
    setMessages([]);
    setReplyTo(null);
    setHasMoreMessages(true);
    setMessagesOffset(0);
    messageIdsRef.current.clear();
    pendingMessagesRef.current.clear();
    processedMessageIdsRef.current.clear(); // ✅ Очищаем при сбросе
  }, []);

  // Загрузка более старых сообщений
  const loadMoreMessages = useCallback(() => {
    if (!currentRoom || !hasMoreMessages || loadingMoreMessages || isLoadingMoreRef.current || !isMounted.current) return;
    
    isLoadingMoreRef.current = true;
    
    const skip = messagesOffset;
    const limit = 50;
    
    loadMessages(currentRoom.id, skip, limit, true)
      .finally(() => {
        setSafeTimeout(() => {
          isLoadingMoreRef.current = false;
        }, 500);
      });
  }, [currentRoom, hasMoreMessages, loadingMoreMessages, messagesOffset, loadMessages, setSafeTimeout, isMounted]);

  // Обработчик печатания
  const sendTypingIndicator = useCallback((roomId, isTyping) => {
    if (!isConnected || !roomId || !isMounted.current) return;
    
    send({
      type: 'chat:typing',
      data: {
        room_id: roomId,
        is_typing: isTyping
      }
    });
  }, [isConnected, send, isMounted]);

  // ✅ ИСПРАВЛЕННЫЙ WebSocket обработчик
  const handleNewMessage = useCallback((data) => {
    // Проверяем дубликаты
    if (processedMessageIdsRef.current.has(data.id)) {
      console.log(`⏭️ Message ${data.id} already processed, skipping`);
      return;
    }
    
    processedMessageIdsRef.current.add(data.id);
    lastProcessedMessageIdRef.current = data.id;

    // СВОИ СООБЩЕНИЯ - только заменяем временные
    if (data.user_id === userIdRef.current) {
      const pendingEntry = Array.from(pendingMessagesRef.current.entries())
        .find(([_, pending]) => {
          const timeDiff = Math.abs(new Date(pending.created_at) - new Date(data.created_at));
          return pending.content === data.content && timeDiff < 3000;
        });

      if (pendingEntry) {
        const [tempId] = pendingEntry;
        
        setMessages(prev => prev.map(msg => 
          msg.id === tempId ? data : msg
        ));
        
        pendingMessagesRef.current.delete(tempId);
        if (data.id) messageIdsRef.current.add(data.id);
      }
      
      // ✅ ВАЖНО: для своих сообщений НЕ увеличиваем счетчики
      return;
    }

    // ЧУЖИЕ СООБЩЕНИЯ
    if (data.id && messageIdsRef.current.has(data.id)) {
      return;
    }

    if (data.id) {
      messageIdsRef.current.add(data.id);
      cleanupMessageIds();
    }

    if (isMounted.current) {
      // Обновляем комнату в списке
      setRooms(prevRooms => {
        const roomIndex = prevRooms.findIndex(r => r.id === data.room_id);
        if (roomIndex === -1) return prevRooms;

        const updatedRoom = {
          ...prevRooms[roomIndex],
          last_message: data,
          updated_at: data.created_at
        };

        const newRooms = [...prevRooms];
        newRooms.splice(roomIndex, 1);
        newRooms.unshift(updatedRoom);
        return newRooms;
      });

      // ✅ ИСПРАВЛЕНО: Проверяем, что мы НЕ в текущей комнате или страница скрыта
      const isCurrentRoom = currentRoom?.id === data.room_id;
      
      if (isCurrentRoom && isVisible) {
        // Если это текущая комната и страница видна - добавляем сообщение
        setMessages(prev => [...prev, data]);
        
        // ✅ Отмечаем как прочитанное сразу
        chatAPI.markRoomAsRead(data.room_id).catch(console.error);
      } else {
        // Во всех остальных случаях - увеличиваем счетчик
        setUnreadCounts(prev => {
          // ✅ Проверяем, что сообщение не от нас
          if (data.user_id === userIdRef.current) {
            return prev; // Не увеличиваем для своих сообщений
          }
          
          const newCount = (prev[data.room_id] || 0) + 1;
          console.log(`🔔 Increasing unread count for room ${data.room_id}: ${newCount}`);
          return { ...prev, [data.room_id]: newCount };
        });
      }
    }
  }, [currentRoom, cleanupMessageIds, isMounted, isVisible]);

  // Обработка возврата на страницу
  useEffect(() => {
    if (isVisible && pendingMarkRead.size > 0) {
      pendingMarkRead.forEach((count, roomId) => {
        if (currentRoom?.id === roomId) {
          chatAPI.markRoomAsRead(roomId).catch(console.error);
        }
      });
      setPendingMarkRead(new Map());
    }
  }, [isVisible, pendingMarkRead, currentRoom]);

  const handleParticipantAdded = useCallback((data) => {
    const { user_id, room_id, role } = data;
    if (!isMounted.current) return;
    
    if (currentRoom?.id === room_id) {
      chatAPI.getRoom(room_id).then(room => {
        if (isMounted.current) {
          setCurrentRoom(room);
          setRooms(prev => prev.map(r => r.id === room_id ? room : r));
        }
      }).catch(console.error);
    } else {
      chatAPI.getRoom(room_id).then(room => {
        if (isMounted.current) {
          setRooms(prev => prev.map(r => r.id === room_id ? room : r));
        }
      }).catch(console.error);
    }
  }, [currentRoom, isMounted]);

  const handleParticipantRemoved = useCallback((data) => {
    const { user_id, room_id } = data;
    if (!isMounted.current) return;
    
    if (user_id === userIdRef.current) {
      setRooms(prev => prev.filter(r => r.id !== room_id));
      if (currentRoom?.id === room_id) {
        setCurrentRoom(null);
        setMessages([]);
        setHasMoreMessages(true);
        setMessagesOffset(0);
        messageIdsRef.current.clear();
        pendingMessagesRef.current.clear();
      }
    } else if (currentRoom?.id === room_id) {
      chatAPI.getRoom(room_id).then(room => {
        if (isMounted.current) {
          setCurrentRoom(room);
          setRooms(prev => prev.map(r => r.id === room_id ? room : r));
        }
      }).catch(console.error);
    }
  }, [currentRoom, isMounted]);

  const handleRoomLocked = useCallback((data) => {
    const { room_id } = data;
    if (!isMounted.current) return;
    
    setRooms(prev => prev.map(room =>
      room.id === room_id ? { ...room, is_locked: true } : room
    ));
    if (currentRoom?.id === room_id) {
      setCurrentRoom(prev => prev ? { ...prev, is_locked: true } : null);
    }
  }, [currentRoom, isMounted]);

  const handleTyping = useCallback((data) => {
    const { room_id, user_id, user_name, is_typing } = data;
    
    if (user_id === userIdRef.current || !isMounted.current) return;
    
    setTypingUsers(prev => {
      const roomTyping = prev[room_id] || new Map();
      
      if (is_typing) {
        roomTyping.set(user_id, { user_name, timestamp: Date.now() });
      } else {
        roomTyping.delete(user_id);
      }
      
      return { ...prev, [room_id]: roomTyping };
    });
  }, [isMounted]);

  const handleMessagesRead = useCallback((data) => {
    if (data.room_id && isMounted.current) {
      setUnreadCounts(prev => {
        const newCount = Math.max(0, (prev[data.room_id] || 0) - (data.count || 1));
        const newCounts = { ...prev, [data.room_id]: newCount };
        if (newCount === 0) {
          delete newCounts[data.room_id];
        }
        return newCounts;
      });
    }
  }, [isMounted]);

  const handleSubscribed = useCallback(() => {}, []);

  // Автоматическая очистка typing индикаторов
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isMounted.current) return;
      
      const now = Date.now();
      setTypingUsers(prev => {
        const newState = { ...prev };
        Object.keys(newState).forEach(roomId => {
          const users = newState[roomId];
          users.forEach((data, userId) => {
            if (now - data.timestamp > CONSTANTS.TIMING.TYPING_TIMEOUT) {
              users.delete(userId);
            }
          });
          if (users.size === 0) {
            delete newState[roomId];
          }
        });
        return newState;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isMounted]);

  // Подписки на WebSocket
  useEffect(() => {
    if (!isConnected || !isMounted.current) return;

    subscribe('subscribed', handleSubscribed);
    subscribe('chat:messages_read', handleMessagesRead);
    subscribe('chat:new_message', handleNewMessage);
    subscribe('chat:participant_added', handleParticipantAdded);
    subscribe('chat:participant_removed', handleParticipantRemoved);
    subscribe('chat:room_locked', handleRoomLocked);
    subscribe('chat:typing', handleTyping);

    return () => {
      unsubscribe('subscribed', handleSubscribed);
      unsubscribe('chat:messages_read', handleMessagesRead);
      unsubscribe('chat:new_message', handleNewMessage);
      unsubscribe('chat:participant_added', handleParticipantAdded);
      unsubscribe('chat:participant_removed', handleParticipantRemoved);
      unsubscribe('chat:room_locked', handleRoomLocked);
      unsubscribe('chat:typing', handleTyping);
      
      // ✅ Очищаем обработанные ID при отписке
      processedMessageIdsRef.current.clear();
    };
  }, [isConnected, subscribe, unsubscribe, handleSubscribed, handleMessagesRead, 
      handleNewMessage, handleParticipantAdded, handleParticipantRemoved, 
      handleRoomLocked, handleTyping, isMounted]);

  // Переподписка при восстановлении соединения
  useEffect(() => {
    if (!isConnected) return;
    
    if (rooms.length > 0) {
      subscribeToAllRooms();
    }
    
    if (currentRoom) {
      subscribeToRoomChannel(currentRoom.id);
    }
  }, [isConnected, rooms, currentRoom, subscribeToAllRooms, subscribeToRoomChannel]);

  // Синхронизация счетчиков при изменении комнат
  useEffect(() => {
    if (rooms.length > 0) {
      const counts = {};
      rooms.forEach(room => {
        if (room.unread_count > 0) {
          counts[room.id] = room.unread_count;
        }
      });
      
      setUnreadCounts(prev => {
        const merged = { ...prev };
        Object.keys(counts).forEach(roomId => {
          merged[roomId] = counts[roomId];
        });
        return merged;
      });
    }
  }, [rooms]);

  // Сброс комнаты при уходе со страницы
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isChatPage = currentPath === '/_admin/chat';
    
    if (!isChatPage && currentRoom) {
      resetCurrentRoom();
    }
  }, [window.location.pathname, currentRoom, resetCurrentRoom]);

  const value = {
    rooms,
    currentRoom,
    messages,
    loadingRooms,
    loadingMessages,
    loadingMoreMessages,
    sendingMessage,
    error,
    unreadCounts,
    totalUnreadCount,
    hasMoreMessages,
    failedMessages,
    typingUsers,
    replyTo,
    setReplyTo,
    loadRooms,
    loadMessages,
    loadMoreMessages,
    sendMessageRest,
    retrySendMessage,
    createGroupRoom,
    getOrCreatePrivateRoom,
    addParticipant,
    removeParticipant,
    leaveRoom,
    selectRoom,
    sendTypingIndicator,
    resetCurrentRoom,
    isPageVisible: isVisible,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};